'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '@dc-shared-web/lib'
import { cva, VariantProps } from 'class-variance-authority'

type TabsContextType = {
  orientation: React.ComponentPropsWithoutRef<
    typeof TabsPrimitive.Root
  >['orientation']
  variant: VariantProps<typeof tabsStyles>['variant']
}

const DEFAULT_CONTEXT_VALUE: TabsContextType = {
  orientation: 'horizontal',
  variant: 'default',
}

/*
  This context holds the orientation and variant of the tabs for later use in the inner components.
*/
const TabsContext = React.createContext<TabsContextType>(DEFAULT_CONTEXT_VALUE)

const useTabsContext = () => {
  const context = React.useContext(TabsContext)

  if (context === undefined) {
    throw new Error('Tabs components must be used within a TabsContext')
  }

  return context
}

const tabsStyles = cva('', {
  variants: {
    orientation: {
      horizontal: '',
      /*
        If the orientation is vertical, we need to set the grid columns to 256px and 1fr. If orientation is horizontal, we don't need to set any grid columns and we just let the divs take the space they need vertically (The default behavior in divs).
      */
      vertical: 'tw-grid tw-grid-cols-[256px_1fr]',
    },
    variant: {
      default: '',
      switched: '',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
  },
})

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> &
    VariantProps<typeof tabsStyles>
>(({ variant, ...props }, ref) => (
  <TabsContext.Provider
    value={{
      orientation: props.orientation || DEFAULT_CONTEXT_VALUE.orientation,
      variant: variant || DEFAULT_CONTEXT_VALUE.variant,
    }}
  >
    <TabsPrimitive.Root
      ref={ref}
      {...props}
      className={tabsStyles({
        orientation: props.orientation,
        variant,
      })}
    />
  </TabsContext.Provider>
))
Tabs.displayName = TabsPrimitive.Root.displayName

const tabsListStyles = cva(
  /* inline-flex so the tabs list can be used inline with other elements. */
  'tw-inline-flex',
  {
    variants: {
      orientation: {
        horizontal: '',
        /* 'items-stretch' is needed so the tabs take the full column width. */
        vertical: 'tw-flex-col tw-items-stretch',
      },
      variant: {
        default: '',
        switched: 'tw-bg-neutral-100 tw-p-1 tw-rounded-lg',
      },
      fullWidth: {
        true: '',
      },
    },
    compoundVariants: [
      {
        /*
          When trying to make the tabs full width:
          - we make sure first the orientation is horizontal
          - we need to set the flex-1 on the child elements so space is shared across all tabs evenly.
        */
        orientation: 'horizontal',
        fullWidth: true,
        className: 'tw-w-full [&>*]:tw-flex-1',
      },
    ],
    defaultVariants: {
      orientation: 'horizontal',
      variant: 'default',
      fullWidth: false,
    },
  }
)

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    /**
      If true, the tabs will take the full width of the container.
      Only works when orientation is 'horizontal'.
    */
    fullWidth?: boolean
  }
>(({ fullWidth, ...props }, ref) => {
  const { orientation, variant } = useTabsContext()

  return (
    <TabsPrimitive.List
      ref={ref}
      {...props}
      className={tabsListStyles({
        orientation,
        variant,
        fullWidth,
      })}
    />
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const tabsTriggerStyles = cva(
  [
    /* internal spacing */
    'tw-px-4 tw-py-2',
    /* display & alignment */
    'tw-inline-flex tw-gap-2 tw-items-center',
    /* font styles: we style text and svg icons */
    'tw-text-sm tw-leading-2 tw-text-neutral-950 [&>svg]:tw-text-neutral-950',
    /* svg icon specific styles */
    '[&>svg]:tw-w-4 [&>svg]:tw-h-4',
    /* animation */
    'tw-transition-all',
    /* focus styles */
    'focus:tw-outline-none focus:tw-ring-2',
    /* active state styles */
    'data-[state=active]:tw-bg-white data-[state=active]:tw-font-medium',
  ],
  {
    variants: {
      orientation: {
        /* justify-center so the tabs are centered. */
        horizontal: 'tw-justify-center',
        vertical: 'tw-text-left',
      },
      variant: {
        default: '',
        switched: 'tw-rounded-md',
      },
    },
    compoundVariants: [
      {
        orientation: 'horizontal',
        variant: 'default',
        className: [
          /* in this variant tabs have a fixed height. */
          'tw-h-9',
          /* The shadow mimics a border bottom to avoid layout shifts when the tabs are hovered or active. */
          'tw-shadow-[inset_0_-1px_0_0_theme(colors.neutral.200)]',
          /* hover styles */
          'hover:tw-bg-neutral-50 hover:tw-shadow-[inset_0_-2px_0_0_theme(colors.blue.400)]',
          /* active styles */
          'data-[state=active]:tw-shadow-[inset_0_-2px_0_0_theme(colors.blue.400)]',
        ],
      },
      {
        orientation: 'horizontal',
        variant: 'switched',
        className: 'tw-h-7',
      },
      {
        orientation: 'vertical',
        variant: 'default',
        className: [
          /* The shadow mimics a border right. */
          'tw-shadow-[1px_0_0_0_theme(colors.neutral.200)]',
          /* hover styles */
          'hover:tw-bg-neutral-50 hover:tw-shadow-[2px_0_0_0_theme(colors.blue.400)]',
          /* active styles */
          'data-[state=active]:tw-shadow-[2px_0_0_0_theme(colors.blue.400)]',
        ],
      },
    ],
    defaultVariants: {
      orientation: 'horizontal',
      variant: 'default',
    },
  }
)

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ ...props }, ref) => {
  const { orientation, variant } = useTabsContext()

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      {...props}
      className={tabsTriggerStyles({
        orientation,
        variant,
      })}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('focus:tw-outline-none focus:tw-ring-2', className)}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
